/**
 * Abschnitt „Modelle" im Admin-Bereich – die Anlagentypen aller Hersteller.
 *
 * Mandantenübergreifende Stammdaten: Was hier steht, ist die Vorlage für jedes Projekt, das
 * dieses Modell verwendet. Schreiben darf deshalb nur die Plattform-Administration; das
 * Backend setzt es durch (ADMIN_WRITE).
 *
 * Die Tabelle zeigt immer alle Modelle, nach Hersteller sortiert; ist im Abschnitt darüber ein
 * Hersteller ausgewählt, bleiben nur dessen Modelle stehen. Die Spalte „Hersteller" bleibt auch
 * dann stehen – sie ist die Hälfte des Namens, unter dem ein Modell bekannt ist.
 *
 * Ein Modell lässt sich nicht löschen, solange Anlagen es als Herkunft tragen – der Fehler
 * kommt vom Server und erscheint als Toast.
 */
import React, { useMemo, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import Section from "../../../layout/Section";
import Button from "../../../common/inputs/Button";
import BaseTable, { TableColumn } from "../../../tables/common/BaseTable";
import AnlagentypDialog from "./AnlagentypDialog";
import { useToast } from "../../../../contexts/ToastContext";
import {
  Anlagentyp,
  AnlagentypCreate,
  AnlagentypUpdate,
  Hersteller,
} from "../../../../services/api/anlagentypen";

const SPALTEN: TableColumn[] = [
  { key: "hersteller", label: "Hersteller", width: "25%" },
  { key: "typ", label: "Modell", width: "25%" },
  { key: "nennleistung", label: "Nennleistung", align: "right", unit: "MW" },
  { key: "rotorradius", label: "Rotorradius", align: "right", unit: "m" },
  { key: "nabenhöhe", label: "Nabenhöhe", align: "right", unit: "m" },
];

interface ModelleSectionProps {
  anlagentypen: Anlagentyp[];
  hersteller: Hersteller[];
  loading: boolean;
  error: string | null;
  /** Filtert die Tabelle auf einen Hersteller, `null` zeigt alle */
  herstellerFilter: string | null;
  onFilterAufheben: () => void;
  onCreate: (daten: AnlagentypCreate) => Promise<Anlagentyp>;
  onUpdate: (id: string, daten: AnlagentypUpdate) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

const ModelleSection: React.FC<ModelleSectionProps> = ({
  anlagentypen,
  hersteller,
  loading,
  error,
  herstellerFilter,
  onFilterAufheben,
  onCreate,
  onUpdate,
  onDelete,
}) => {
  const { showToast } = useToast();
  const [dialogOffen, setDialogOffen] = useState(false);
  const [bearbeitet, setBearbeitet] = useState<Anlagentyp | null>(null);

  const gefiltert = useMemo(
    () =>
      herstellerFilter
        ? anlagentypen.filter((a) => a.hersteller_id === herstellerFilter)
        : anlagentypen,
    [anlagentypen, herstellerFilter],
  );

  const gefilterterName = herstellerFilter
    ? (hersteller.find((h) => h.id === herstellerFilter)?.name ?? null)
    : null;

  const oeffnen = (anlagentyp: Anlagentyp | null) => {
    setBearbeitet(anlagentyp);
    setDialogOffen(true);
  };

  const loeschen = async (id: string) => {
    try {
      await onDelete(id);
      showToast("Modell gelöscht", "success");
    } catch {
      showToast(
        "Das Modell wird von Anlagen verwendet und kann nicht gelöscht werden.",
        "error",
      );
    }
  };

  return (
    <Section
      title={gefilterterName ? `Modelle – ${gefilterterName}` : "Modelle"}
      noPadding
      actions={
        <>
          {herstellerFilter && (
            <Button
              variant="secondary"
              onClick={onFilterAufheben}
              sx={{ mr: 1 }}
            >
              Alle Hersteller
            </Button>
          )}
          <Button startIcon={<AddIcon />} onClick={() => oeffnen(null)}>
            Modell anlegen
          </Button>
        </>
      }
    >
      <BaseTable
        columns={SPALTEN}
        data={gefiltert}
        loading={loading}
        fixedLayout
        emptyMessage={
          error ??
          (herstellerFilter
            ? "Zu diesem Hersteller ist noch kein Modell erfasst."
            : "Noch keine Modelle erfasst.")
        }
        onRowClick={(row) =>
          oeffnen(anlagentypen.find((a) => a.id === row.id) ?? null)
        }
        onDelete={(row) => void loeschen(row.id)}
      />
      <AnlagentypDialog
        open={dialogOffen}
        anlagentyp={bearbeitet}
        hersteller={hersteller}
        vorauswahlHerstellerId={herstellerFilter}
        onClose={() => setDialogOffen(false)}
        onCreate={onCreate}
        onUpdate={onUpdate}
      />
    </Section>
  );
};

export default ModelleSection;
